import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  WorkspaceChrome,
  WorkspaceSummary,
  WorkspaceActions,
  WorkspaceSection,
  WorkspaceStat,
  tabFromQuery,
} from '../../../platform/workspace360';
import RecordNavigation from '../../../components/common/recordNavigation';
import { saveComment, getComments } from '../../../services/commentService';
import { saveTicket } from '../../../services/ticketService';
import {
  TICKET360_TABS,
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_CATEGORY_LABELS,
} from './ticket360TabIds';

class Ticket360Panel extends Component {
  state = {
    activeTab: 'conversation',
    comments: [],
    loadingComments: false,
    draft: '',
    saving: false,
    focusComposerToken: 0,
  };

  composerRef = null;
  textareaRef = null;

  componentDidMount() {
    this.syncTabFromLocation();
    this.loadComments();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location?.search !== this.props.location?.search) {
      this.syncTabFromLocation();
    }
    if (prevProps.ticket?.ticketid !== this.props.ticket?.ticketid) {
      this.loadComments();
    }
    if (
      this.state.activeTab === 'conversation' &&
      prevState.focusComposerToken !== this.state.focusComposerToken
    ) {
      this.focusComposer();
    }
  }

  syncTabFromLocation = () => {
    const activeTab = tabFromQuery(
      this.props.location?.search,
      'conversation',
      TICKET360_TABS
    );
    this.setState({ activeTab });
  };

  loadComments = async () => {
    const ticketid = this.props.ticket?.ticketid;
    if (!ticketid) return;
    this.setState({ loadingComments: true });
    try {
      const { data: comments } = await getComments(ticketid);
      const rows = (comments || []).map((obj) => ({
        id: obj.id,
        createdby: obj.author ? obj.author.fullname : 'Unknown',
        contacttype: obj.author ? obj.author.contacttype : 'Unknown',
        createdat: moment(obj.createdAt).calendar(),
        comment: obj.comment,
      }));
      this.setState({ comments: rows, loadingComments: false });
    } catch (_) {
      this.setState({ comments: [], loadingComments: false });
    }
  };

  handleTabChange = (tabId) => {
    this.setState({ activeTab: tabId });
    const search = tabId === 'conversation' ? '' : `?tab=${tabId}`;
    this.props.history.replace({ pathname: this.props.location.pathname, search });
  };

  focusComposer = () => {
    const scroll = () => {
      if (this.composerRef && typeof this.composerRef.scrollIntoView === 'function') {
        this.composerRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (this.textareaRef && typeof this.textareaRef.focus === 'function') {
        this.textareaRef.focus();
      }
    };
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => setTimeout(scroll, 0));
    } else {
      setTimeout(scroll, 0);
    }
  };

  openComposer = () => {
    this.handleTabChange('conversation');
    this.setState((prev) => ({
      focusComposerToken: prev.focusComposerToken + 1,
    }));
  };

  submitComment = async () => {
    const { ticket, user, onTicketUpdated } = this.props;
    const draft = String(this.state.draft || '').trim();
    if (!draft || !ticket?.ticketid) return;

    this.setState({ saving: true });
    try {
      let nextTicket = { ...ticket };
      if (Number(nextTicket.status) === 3) {
        nextTicket = { ...nextTicket, status: 4, alertemail: false };
        await saveTicket(nextTicket);
        if (onTicketUpdated) onTicketUpdated(nextTicket);
      }

      await saveComment({
        documentid: ticket.ticketid,
        comment: draft,
        createdby: user?.contactId || user?.id,
        source: 1,
      });

      this.setState({ draft: '', saving: false });
      await this.loadComments();
      toast.success('Reply sent.');
    } catch (ex) {
      this.setState({ saving: false });
      toast.error(
        ex.response && ex.response.data
          ? `Error: ${ex.response.data}`
          : 'Failed to send reply.'
      );
    }
  };

  renderConversation() {
    const { comments, loadingComments, draft, saving } = this.state;
    return (
      <WorkspaceSection title="Conversation" icon="fa-comments">
        {loadingComments && <p className="customer360-muted small mb-0">Loading…</p>}
        {!loadingComments && !comments.length && (
          <p className="customer360-muted mb-0">No messages yet. Add a reply to continue the conversation.</p>
        )}
        <div className="portal360-thread">
          {comments.map((c, idx) => (
            <div key={c.id || idx} className="portal360-thread-item">
              <div className="d-flex justify-content-between">
                <strong className="small text-secondary">{c.createdby}</strong>
                <small className="customer360-muted">{c.createdat}</small>
              </div>
              <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                {c.comment}
              </div>
              <span
                className={`badge badge-pill badge-${
                  c.contacttype === 'employee' ? 'info' : 'secondary'
                } mt-1`}
              >
                {c.contacttype
                  ? c.contacttype.charAt(0).toUpperCase() + c.contacttype.slice(1)
                  : 'Unknown'}
              </span>
            </div>
          ))}
        </div>
        <div
          className="portal360-composer mt-3"
          ref={(el) => {
            this.composerRef = el;
          }}
        >
          <textarea
            className="form-control mb-2"
            value={draft}
            onChange={(e) => this.setState({ draft: e.target.value })}
            placeholder="Write your reply…"
            ref={(el) => {
              this.textareaRef = el;
            }}
          />
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={saving || !draft.trim()}
            onClick={this.submitComment}
          >
            <i className="fa fa-paper-plane mr-1" aria-hidden /> Send
          </button>
        </div>
      </WorkspaceSection>
    );
  }

  renderOverview() {
    const { ticket } = this.props;
    return (
      <WorkspaceSection title="Overview" icon="fa-info-circle">
        <div className="customer360-grid-3">
          <WorkspaceStat label="Subject" value={ticket.subject} isStatic />
          <WorkspaceStat label="Status" value={TICKET_STATUS_LABELS[ticket.status]} isStatic />
          <WorkspaceStat label="Priority" value={TICKET_PRIORITY_LABELS[ticket.priority]} isStatic />
          <WorkspaceStat label="Category" value={TICKET_CATEGORY_LABELS[ticket.category]} isStatic />
          <WorkspaceStat label="Account" value={ticket.account} isStatic />
          <WorkspaceStat label="Due" value={ticket.duedate} isStatic />
        </div>
        {ticket.message ? (
          <div className="mt-3">
            <div className="customer360-snapshot__stat-label">Original message</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</div>
          </div>
        ) : null}
      </WorkspaceSection>
    );
  }

  renderRelated() {
    const { ticket } = this.props;
    return (
      <WorkspaceSection title="Related" icon="fa-link">
        <div className="customer360-grid-3">
          <WorkspaceStat
            label="Account"
            value={
              ticket.account ? (
                <Link to={`/accounts/${ticket.account}`}>{ticket.account}</Link>
              ) : null
            }
            isStatic
          />
          <WorkspaceStat label="Ticket ID" value={ticket.ticketid} isStatic />
        </div>
      </WorkspaceSection>
    );
  }

  render() {
    const { ticket, history, location, navPaths } = this.props;
    if (!ticket || !ticket.id) return null;

    const { activeTab } = this.state;
    const title =
      (ticket.subject && String(ticket.subject).trim()) ||
      (ticket.ticketid ? `Ticket ${ticket.ticketid}` : `Ticket #${ticket.id}`);
    const subtitle = [
      TICKET_STATUS_LABELS[ticket.status] || null,
      TICKET_PRIORITY_LABELS[ticket.priority] || null,
      ticket.ticketid ? `#${ticket.ticketid}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

    const summary = (
      <WorkspaceSummary
        title="Ticket Overview"
        icon="fa-life-ring"
        stats={[
          {
            key: 'status',
            label: 'Status',
            value: TICKET_STATUS_LABELS[ticket.status] || '—',
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'priority',
            label: 'Priority',
            value: TICKET_PRIORITY_LABELS[ticket.priority] || '—',
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'account',
            label: 'Account',
            value: ticket.account || '—',
            onClick: () => this.handleTabChange('related'),
            isActive: activeTab === 'related',
          },
          {
            key: 'messages',
            label: 'Messages',
            value: this.state.comments.length,
            onClick: () => this.handleTabChange('conversation'),
            isActive: activeTab === 'conversation',
          },
        ]}
      />
    );

    const actions = (
      <WorkspaceActions>
        <button
          type="button"
          className="btn btn-light btn-sm"
          onClick={this.openComposer}
        >
          <i className="fa fa-reply mr-1" aria-hidden /> Reply
        </button>
      </WorkspaceActions>
    );

    let body = null;
    if (activeTab === 'overview') body = this.renderOverview();
    else if (activeTab === 'related') body = this.renderRelated();
    else body = this.renderConversation();

      return (
      <WorkspaceChrome
        title={title}
        subtitle={subtitle}
        listLabel="Tickets"
        listTo="/tickets"
        recordLabel={title}
        nav={
          <RecordNavigation
            history={history}
            currentPath={location.pathname}
            paths={navPaths || []}
          />
        }
        summary={summary}
        actions={actions}
        tabs={TICKET360_TABS}
        activeTab={activeTab}
        onTabChange={this.handleTabChange}
      >
        {body}
      </WorkspaceChrome>
    );
  }
}

Ticket360Panel.propTypes = {
  ticket: PropTypes.object,
  user: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navPaths: PropTypes.array,
  onTicketUpdated: PropTypes.func,
};

export default Ticket360Panel;
